import { isPlatformBrowser, Location } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TagManagerService } from '@app/app/common/tag.service';
import { forkJoin } from 'rxjs';
import { TranslateExtendedService } from 'src/app/common/translate-extended.service';
import { PoiDetail } from 'src/app/gis/model/poidetail/poidetail';

@Component({
  selector: 'app-detail-tabs',
  templateUrl: './detail-tabs.component.html',
})
export class DetailTabsComponent implements AfterViewInit, OnInit {
  @Input() poi: PoiDetail;
  public activeTab = '';

  private tabChangeListener: (event: CustomEvent) => void;

  private tabsToId: { id: string; key: string }[] = [
    { id: 'mp24-boxes', key: 'detail.mp24.boxes' },
    { id: 'addresses', key: 'detail.addressing.pickpostAndPostlagernd' },
    { id: 'opening-hours', key: 'detail.hours.hours' },
    { id: 'deadlines', key: 'detail.hours.deadline' },
    { id: 'services', key: 'detail.services.service' },
    { id: 'partners', key: 'detail.partners.offers' },
  ];

  constructor(
    private readonly translateExtendedService: TranslateExtendedService,
    private readonly tagManagerService: TagManagerService,
    private route: ActivatedRoute,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  ngOnInit(): void {
    this.activeTab = this.route.snapshot.fragment;

    if (!this.activeTab || !this.isValidActiveTab(this.activeTab)) {
      this.activeTab = this.getDefaultTab(this.poi);
      const url = this.location.path().split('#')[0] + `#${this.activeTab}`;
      this.location.go(url);
    }
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const tabs = document.querySelector('post-tabs') as any;
    this.tabChangeListener = (event: CustomEvent) => {
      this.activeTab = event.detail as string;
      const url = this.location.path().split('#')[0] + `#${this.activeTab}`;
      this.location.go(url);

      forkJoin([
        this.translateExtendedService.get(this.getTabTranslationByKey(event.detail)),
        this.translateExtendedService.getGermanTranslation(this.getTabTranslationByKey(event.detail)),
      ]).subscribe(([navTitle, navTitleGer]) => {
        /* eslint-disable @typescript-eslint/naming-convention */
        this.tagManagerService.click({
          event: 'select_menu',
          type: 'tab',
          label: navTitleGer.toLowerCase().replace(/\s/g, '-'),
          text: navTitle.toLowerCase(),
          additional_info: 'poi',
        });
        /* eslint-enable @typescript-eslint/naming-convention */
      });
    };

    tabs.addEventListener('postChange', this.tabChangeListener);
  }

  public getTabTranslationByKey(id: string) {
    return this.tabsToId.find((e) => e.id === id).key;
  }

  private isValidActiveTab(tab: string): boolean {
    return this.tabsToId.some((e) => e.id === tab);
  }

  private getDefaultTab(poi: PoiDetail): string {
    // !make sure the default tab order is in line with the tab order in the template
    if (poi.hasMp24Boxes) {
      return 'mp24-boxes';
    }
    if (poi.hasAddresses) {
      return 'addresses';
    }
    if (poi.hasOpeningHours) {
      return 'opening-hours';
    }
    if (poi.hasDeadlines) {
      return 'deadlines';
    }
    if (poi.hasServices) {
      return 'services';
    }
    if (poi.hasPartners) {
      return 'partners';
    }

    // fallback, hopyfully not needed
    console.warn('No valid default tab found for POI, falling back to addresses tab');
    return 'addresses';
  }
}
